<template>
    <div>
        <section class="basic-dashboard-summary">
            <button class="basic-summary-card basic-summary-card--action" type="button" @click="showHistoryDialog = true">
                <span class="basic-summary-card__kicker">History</span>
                <span class="basic-summary-card__title">View previous forms</span>
                <span class="basic-summary-card__meta">{{ formHistory.length }} responses found</span>
            </button>

            <button
                class="basic-summary-card basic-summary-card--action"
                type="button"
                :disabled="!latestCompletedForm"
                @click="openLatestCompletedForm"
            >
                <span class="basic-summary-card__kicker">Latest completed</span>
                <span class="basic-summary-card__title">View latest completed form</span>
                <span class="basic-summary-card__meta">
                    {{ latestCompletedForm ? formatDate(latestCompletedForm.completed_on) : 'No completed forms yet' }}
                </span>
            </button>

            <div class="basic-summary-card" :class="{ 'basic-summary-card--overdue': daysSinceLastForm > 90 }">
                <span class="basic-summary-card__kicker">Days since last form</span>
                <span class="basic-summary-card__value">{{ daysSinceLastForm }}</span>
            </div>
        </section>

        <section class="basic-dashboard-content">
            <div class="basic-panel">
                <div class="basic-panel__eyebrow">Latest completed form</div>
                <div v-if="loadingHistory" class="basic-panel__state">
                    <q-spinner color="primary" size="32px" />
                </div>
                <div v-else-if="historyError" class="basic-panel__state text-negative">
                    {{ historyError }}
                </div>
                <div v-else-if="latestCompletedForm" class="basic-panel__body">
                    <div class="basic-panel__title">{{ selectedFormTitle }}</div>
                    <div class="basic-panel__meta">Completed {{ formatDate(latestCompletedForm.completed_on) }}</div>
                    <div class="basic-panel__meta">Status: {{ capitalise(latestCompletedForm.status || 'complete') }}</div>
                    <q-btn
                        class="q-mt-md"
                        color="primary"
                        no-caps
                        unelevated
                        label="Open completed response"
                        @click="openLatestCompletedForm"
                    />
                </div>
                <div v-else class="basic-panel__state">
                    No completed forms are available for this client yet.
                </div>
            </div>

            <div class="basic-panel">
                <div class="basic-panel__eyebrow">Previous responses</div>
                <div v-if="loadingHistory" class="basic-panel__state">
                    <q-spinner color="primary" size="32px" />
                </div>
                <div v-else-if="historyError" class="basic-panel__state text-negative">
                    {{ historyError }}
                </div>
                <div v-else-if="formHistory.length" class="basic-panel__body">
                    <div class="basic-panel__title">{{ formHistory.length }} responses found</div>
                    <div class="basic-panel__meta">
                        Latest activity {{ formatDate(formHistory[0].updated_at || formHistory[0].started_at) }}
                    </div>
                    <q-btn
                        class="q-mt-md"
                        outline
                        color="primary"
                        no-caps
                        label="Browse previous forms"
                        @click="showHistoryDialog = true"
                    />
                </div>
                <div v-else class="basic-panel__state">
                    No responses were found for this client and form type.
                </div>
            </div>
        </section>

        <q-dialog v-model="showHistoryDialog" @hide="handleHistoryDialogHide">
            <q-card class="basic-dialog">
                <q-card-section class="row items-center justify-between">
                    <div class="text-h6">Previous Form Responses</div>
                    <q-btn flat round dense icon="close" v-close-popup />
                </q-card-section>
                <q-card-section>
                    <PreviousFormsTable
                        :subject="subject"
                        :selected-form-id="selectedFormId"
                        :allow-assign="false"
                        @form-selected="openResponse"
                    />
                </q-card-section>
            </q-card>
        </q-dialog>

        <q-dialog v-model="showResponseDialog" @hide="handleResponseDialogHide">
            <q-card class="bg-white basic-response-dialog">
                <q-card-section class="basic-response-dialog__body">
                    <div class="basic-response-dialog__content">
                        <DynamicFormRenderer
                            v-if="showResponseDialog && selectedResponseId !== null"
                            :key="`${selectedFormId}-${selectedResponseId}`"
                            :formId="selectedFormId"
                            :subject="subject"
                            :_responseId="selectedResponseId"
                            @exit="showResponseDialog = false"
                            @form-submitted="handleFormSubmitted"
                        />
                    </div>
                </q-card-section>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import api from "@/services/api/api";
import PreviousFormsTable from "@/components/DynamicFormComponents/PreviousFormsTable.vue";
import DynamicFormRenderer from "@/components/DynamicFormComponents/DynamicFormRenderer.vue";
import { subjectFromClient, hasLoadableHistory } from "@/services/util/formSubject";

export default {
    name: 'BasicFormDashboard',

    components: {
        DynamicFormRenderer,
        PreviousFormsTable
    },

    props: {
        selectedClient: {
            type: Object,
            required: true
        },
        selectedFormId: {
            type: [Number, String],
            required: true
        },
        selectedFormTitle: {
            type: String,
            default: 'Client Form'
        }
    },

    emits: ['refresh-clients'],

    data() {
        return {
            loadingHistory: false,
            historyError: null,
            formHistory: [],
            showHistoryDialog: false,
            showResponseDialog: false,
            selectedResponseId: null
        };
    },

    computed: {
        // Form subject (Paua or temporary client) for the dynamic-forms components.
        subject() {
            return subjectFromClient(this.selectedClient);
        },

        completedResponses() {
            return [...this.formHistory]
                .filter((response) => response?.completed_on)
                .sort((left, right) => new Date(right.completed_on) - new Date(left.completed_on));
        },

        latestCompletedForm() {
            return this.completedResponses[0] || null;
        },

        daysSinceLastForm() {
            const latestDate = this.latestCompletedForm?.completed_on || this.selectedClient?.latest_submitted_time;
            if (!latestDate) return 0;

            const timestamp = new Date(latestDate).getTime();
            if (Number.isNaN(timestamp)) return 0;

            return Math.floor((Date.now() - timestamp) / 86400000);
        }
    },

    watch: {
        selectedClient: {
            immediate: true,
            handler() {
                this.refreshDashboardData();
            }
        },
        selectedFormId() {
            this.refreshDashboardData();
        }
    },

    methods: {
        async refreshDashboardData() {
            this.historyError = null;
            this.formHistory = [];

            if (!hasLoadableHistory(this.subject) || !this.selectedFormId) {
                return;
            }

            await this.loadFormHistory();
        },

        async loadFormHistory() {
            this.loadingHistory = true;

            try {
                const response = await api.forms.getFormHistory(this.selectedFormId, this.subject);
                this.formHistory = response.data?.data || [];
            } catch (error) {
                this.historyError = 'Failed to load form history.';
            } finally {
                this.loadingHistory = false;
            }
        },

        formatDate(dateString) {
            if (!dateString) return 'N/A';
            return new Date(dateString).toLocaleString('en-nz');
        },

        capitalise(value) {
            if (typeof value !== 'string') return '';

            return value
                .replace(/_/g, ' ')
                .split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        },

        openLatestCompletedForm() {
            if (!this.latestCompletedForm?.id) return;
            this.openResponse(this.latestCompletedForm.id);
        },

        openResponse(responseId) {
            if (!responseId) return;

            this.selectedResponseId = responseId;
            this.showHistoryDialog = false;
            this.showResponseDialog = true;
        },

        async handleHistoryDialogHide() {
            await this.loadFormHistory();
            this.$emit('refresh-clients');
        },

        async handleResponseDialogHide() {
            this.selectedResponseId = null;
            await this.loadFormHistory();
            this.$emit('refresh-clients');
        },

        async handleFormSubmitted() {
            await this.loadFormHistory();
            this.$emit('refresh-clients');
        }
    }
};
</script>

<style scoped>
.basic-dashboard-summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    padding: 20px;
    background: white;
    border-bottom: 1px solid rgba(47, 62, 70, 0.08);
}

.basic-summary-card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 92px;
    padding: 16px;
    border: 0;
    border-radius: 12px;
    text-align: left;
    background: linear-gradient(160deg, rgba(132, 169, 140, 0.08), rgba(255, 255, 255, 0.96));
    box-shadow: 0 8px 25px rgba(47, 62, 70, 0.08);
}

.basic-summary-card--action {
    cursor: pointer;
}

.basic-summary-card--action:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.basic-summary-card--overdue {
    background: linear-gradient(160deg, rgba(244, 190, 104, 0.14), rgba(255, 255, 255, 0.96));
}

.basic-summary-card__kicker {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #2f3e46;
}

.basic-summary-card__title {
    margin-top: 8px;
    font-size: 1.05rem;
    font-weight: 600;
    color: #25434a;
}

.basic-summary-card__meta {
    margin-top: 10px;
    color: #607175;
    line-height: 1.4;
    font-size: 14px;
}

.basic-summary-card__value {
    margin-top: 12px;
    font-size: 1.8rem;
    font-weight: 600;
    line-height: 1;
    color: #25434a;
}

.basic-dashboard-content {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
    padding: 20px;
}

.basic-panel {
    min-height: 260px;
    padding: 20px;
    border-radius: 20px;
    background: linear-gradient(180deg, rgba(237, 244, 239, 0.95), rgba(255, 255, 255, 1));
    box-shadow: 0 8px 25px rgba(47, 62, 70, 0.08);
}

.basic-panel__eyebrow {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #2f3e46;
}

.basic-panel__body {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 180px;
}

.basic-panel__title {
    margin-top: 18px;
    font-size: 1.25rem;
    font-weight: 600;
    color: #25434a;
}

.basic-panel__meta {
    margin-top: 10px;
    color: #607175;
    line-height: 1.5;
}

.basic-panel__state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    text-align: center;
    color: #607175;
}

.basic-dialog {
    width: min(1200px, 95vw);
    max-width: 95vw;
}

.basic-response-dialog {
    width: min(1700px, 96vw);
    max-width: 96vw;
    height: min(92vh, 1400px);
    max-height: 92vh;
}

.basic-response-dialog__body {
    height: 100%;
    overflow: auto;
    display: flex;
    justify-content: center;
    padding: 24px;
}

.basic-response-dialog__content {
    width: min(1500px, 100%);
}

@media (max-width: 900px) {
    .basic-dashboard-summary,
    .basic-dashboard-content {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 700px) {
    .basic-dashboard-summary,
    .basic-dashboard-content {
        padding: 16px 12px;
    }

    .basic-response-dialog__body {
        padding: 12px;
    }
}
</style>
